import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { ROOMS, TOWN_LOCATIONS } from '../rooms';
import { listWorldObjects, createWorldObject, updateWorldObject, deleteWorldObject } from '../services/worldObjects';

// VenuePlacePanel — controls for the footprint brush (VenuePlacer).
//
// Pick a venue, hover the map, click to drop, Save to commit. Unsaved until you
// press Save for the same reason the building grid is: the first few placements
// are always wrong, and committing each one immediately means rows to clean up
// rather than a bad run you can walk away from.

const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 236, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.86)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
// Turn by whole degrees and land back on a clean number. Adding raw radians
// accumulates float drift, so "⟲ 90° four times" ends up at 359.9999° and the
// readout never quite returns to 0.
const turn = (rad, byDeg) => {
  const d = Math.round((rad * 180) / Math.PI) + byDeg;
  return ((d % 360 + 360) % 360) * (Math.PI / 180);
};

const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};

export default function VenuePlacePanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'venues';
  const room = useWorldStore((s) => s.venuePlaceRoom);
  const setRoom = useWorldStore((s) => s.setVenuePlaceRoom);
  const sizes = useWorldStore((s) => s.venueFootprints);
  const hover = useWorldStore((s) => s.venuePlaceHover);
  const draft = useWorldStore((s) => s.venuePlaceDraft);
  // Read up here with the other selectors, not below the `if (!ON) return null`
  // — a hook after an early return is a hook that sometimes does not run.
  const setDraft = useWorldStore((s) => s.setVenuePlaceDraft);
  const clearDraft = useWorldStore((s) => s.clearVenuePlaceDraft);
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const setOverrides = useWorldStore((s) => s.setVenueAnchorOverrides);
  const planView = useWorldStore((s) => s.planView);
  const setPlanView = useWorldStore((s) => s.setPlanView);
  const editTarget = useWorldStore((s) => s.venueEditTarget);
  const setEditTarget = useWorldStore((s) => s.setVenueEditTarget);
  const shellOverrides = useWorldStore((s) => s.venueShellOverrides);
  const setShellOverrides = useWorldStore((s) => s.setVenueShellOverrides);
  // Read WITH the other selectors. Below the early return it would be a hook
  // that sometimes does not run — the trap this file already warns about once.
  const interiorNow = useWorldStore((s) => s.interior);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [rows, setRows] = useState([]);
  const [shellRows, setShellRows] = useState([]);

  // The saved rows, so Save knows whether to POST or PATCH. Loaded whenever the
  // tool opens rather than once at boot: this is admin-only state and the tool
  // is where it matters.
  useEffect(() => {
    if (!ON) return undefined;
    let dead = false;
    listWorldObjects('street', 'venue_anchor').then((r) => { if (!dead) setRows(r || []); });
    listWorldObjects('street', 'venue_shell').then((r) => { if (!dead) setShellRows(r || []); });
    return () => { dead = true; };
  }, [ON]);

  if (!ON) return null;

  const pending = Object.keys(draft);

  const save = async () => {
    if (!pending.length) return;
    setBusy(true); setMsg('');
    try {
      const next = { ...overrides };
      const nextShell = { ...shellOverrides };
      for (const id of pending) {
        // THE SHELL'S TRIM IS ITS OWN ROW. Written first and independently, so
        // resetting a venue's position never silently discards the alignment
        // work done on its exterior — and vice versa.
        const d = draft[id] || {};
        if (d.shellY !== undefined || d.shellScale !== undefined) {
          const sy = d.shellY ?? shellOverrides[id]?.y ?? 0;
          const ss = d.shellScale ?? shellOverrides[id]?.scale ?? 1;
          const sBody = { zone_id: 'street', kind: 'venue_shell', ref: id, pos: [0, sy, 0], rotY: 0, size: ss };
          const had = shellRows.find((r) => r.ref === id);
          const savedShell = had ? await updateWorldObject(had.object_id, sBody) : await createWorldObject(sBody);
          if (savedShell) {
            setShellRows((prev) => [...prev.filter((r) => r.ref !== id), savedShell]);
            nextShell[id] = { y: sy, scale: ss };
          }
        }
        // A draft may carry only a scale (slider moved, never re-dropped), so
        // fall back to wherever the venue already is rather than saving a 0,0.
        const cur = { ...(overrides[id] || {}), ...draft[id] };
        const x = cur.x ?? 0; const z = cur.z ?? 0; const y = cur.y ?? 0;
        const sc = cur.scale ?? 1;
        // Falls back to the rooms.js default rather than 0, so saving a venue
        // whose facing was only ever authored in code does not silently
        // straighten it out.
        const rot = cur.rotY ?? ROOMS[id]?.modelRotY ?? 0;
        const body = { zone_id: 'street', kind: 'venue_anchor', ref: id, pos: [x, y, z], rotY: rot, size: sc };
        const existing = rows.find((r) => r.ref === id);
        const saved = existing ? await updateWorldObject(existing.object_id, body) : await createWorldObject(body);
        if (saved) {
          setRows((prev) => [...prev.filter((r) => r.ref !== id), saved]);
          next[id] = { x, y, z, scale: sc, rotY: rot };
        }
      }
      setOverrides(next);
      setShellOverrides(nextShell);
      clearDraft();
      setMsg(`saved ${pending.length}`);
    } catch (e) {
      setMsg(e.message || 'save failed');
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3500); }
  };

  // Put a venue back where the sampler / anchorMesh would have put it.
  const reset = async () => {
    if (!room) return;
    const existing = rows.find((r) => r.ref === room);
    setBusy(true);
    try {
      if (existing) await deleteWorldObject(existing.object_id);
      setRows((prev) => prev.filter((r) => r.ref !== room));
      const next = { ...overrides }; delete next[room];
      setOverrides(next);
      setMsg('reset — reload to re-sample');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); setTimeout(() => setMsg(''), 3500); }
  };

  // `sizes` is the BASE footprint (MeshInterior publishes it unscaled, so the
  // slider cannot feed on its own output); the panel applies the slider itself.
  const fp = room ? sizes?.[room] : null;
  const scale = (room ? (draft[room]?.scale ?? overrides?.[room]?.scale) : null) ?? 1;
  // Draft wins, then the saved override. Both may be absent on a venue that has
  // never been moved — the fields then read blank rather than a misleading 0,0.
  const pos = room ? { ...(overrides?.[room] || {}), ...(draft[room] || {}) } : {};
  // Radians in the store and the DB, degrees only here. Normalised into 0..359
  // so the slider never has a negative or a 720° to show after a few ⟲ presses.
  const rotY = pos.rotY ?? ROOMS[room]?.modelRotY ?? 0;
  const deg = Math.round(((rotY * 180) / Math.PI % 360 + 360) % 360);
  const over = hover?.fits;

  // ── WHICH HALF AM I EDITING? ────────────────────────────────────────────
  //
  // A venue is two models, and height and size have to be settable on each — the
  // last centimetres between a shell and its interior are two different pieces
  // of geometry with two different origins, and no rule closes that gap.
  //
  // Rather than a second set of fields nobody would know when to use, the panel
  // acts on WHAT YOU ARE LOOKING AT: standing in the room it moves the room,
  // standing outside it moves the shell. Adjust what you can see, save what you
  // adjusted. The chip pins it when you want to trim the shell from indoors.
  const inRoom = interiorNow === room;
  const target = editTarget === 'auto' ? (inRoom ? 'scene' : 'shell') : editTarget;
  const editingShell = target === 'shell' && !!ROOMS[room]?.shell;

  // The Y and Scale controls read and write different fields depending on the
  // target; everything else (X/Z, facing) always moves the venue as a whole,
  // because a building and its interior are never at different addresses.
  const shellY = draft[room]?.shellY ?? shellOverrides?.[room]?.y ?? ROOMS[room]?.shellY ?? 0;
  const shellScale = draft[room]?.shellScale ?? shellOverrides?.[room]?.scale ?? 1;
  const yValue = editingShell ? shellY : (pos.y ?? 0);
  const scaleValue = editingShell ? shellScale : scale;
  const setY = (v) => setDraft(room, editingShell
    ? { shellY: Math.round(v * 100) / 100 }
    : { x: pos.x ?? 0, z: pos.z ?? 0, y: Math.round(v * 100) / 100 });
  const setScale = (v) => setDraft(room, editingShell ? { shellScale: v } : { scale: v });

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>
        Venue anchors
      </div>

      {!planView && (
        <button style={{ ...btn, width: '100%', marginBottom: 9, background: 'rgba(157,255,125,0.14)', color: '#c8ffb8', borderColor: 'rgba(157,255,125,0.4)' }}
          onClick={() => setPlanView(true)}>
          Turn on Build view
        </button>
      )}

      <div style={{ fontSize: 10.5, opacity: 0.6, marginBottom: 4 }}>Brush — pick a venue, then hover the map</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 9 }}>
        {TOWN_LOCATIONS.map((loc) => {
          const id = loc.interiorId;
          const s = sizes?.[id];
          const on = room === id;
          return (
            <button key={id} onClick={() => setRoom(id)} disabled={!s}
              title={s ? '' : 'Walk into this venue once so its footprint can be measured'}
              style={{ ...btn, fontSize: 11, padding: '6px 8px', textAlign: 'left',
                display: 'flex', justifyContent: 'space-between', gap: 6,
                opacity: s ? 1 : 0.4, cursor: s ? 'pointer' : 'not-allowed',
                background: on ? loc.accent : 'rgba(255,178,94,0.12)',
                color: on ? '#1c1206' : '#ffd27d',
                borderColor: on ? loc.accent : 'rgba(255,178,94,0.35)' }}>
              <span>{loc.name}{ROOMS[id]?.placement === 'sited' ? ' ·' : ''}{draft[id] ? ' •' : ''}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>
                {s ? `${s[0].toFixed(0)}×${s[2].toFixed(0)}` : 'unmeasured'}
              </span>
            </button>
          );
        })}
      </div>

      {/* EXACT POSITION. The click is the fast way; this is the one that always
          works — nudging a venue off a kerb, or undoing a drop that landed off
          the edge of the map, is a job for numbers rather than for finding a
          pixel. Also the only readout that tells you WHERE a venue currently is
          without hovering it. */}
      {room && (
        <div style={{ marginBottom: 9 }}>
          {/* WHAT Y AND SCALE ACT ON. Follows where you are standing by default,
              which is the whole idea — adjust what you can see. Tap to pin it
              when you want to trim the shell without walking outside. */}
          {ROOMS[room]?.shell && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 7, alignItems: 'center' }}>
              {[['scene', 'Scene'], ['shell', 'Shell']].map(([k, label]) => {
                const on = target === k;
                return (
                  <button key={k}
                    onClick={() => setEditTarget(editTarget === k ? 'auto' : k)}
                    title={editTarget === k ? 'Pinned — tap to follow where you stand' : 'Tap to pin'}
                    style={{ ...btn, flex: 1, padding: '5px 0', fontSize: 11,
                      background: on ? '#9dff7d' : 'rgba(157,255,125,0.10)',
                      color: on ? '#0c1c06' : '#c8ffb8',
                      borderColor: on ? '#9dff7d' : 'rgba(157,255,125,0.3)' }}>
                    {label}{editTarget === k ? ' 📌' : ''}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10.5, opacity: 0.7, marginBottom: 3 }}>
            Position{editingShell ? ' · Y and Scale are moving the SHELL' : (ROOMS[room]?.shell ? ' · Y and Scale are moving the ROOM' : '')}
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            {['x', 'y', 'z'].map((axis) => (
              <label key={axis} style={{ flex: 1, fontSize: 10, opacity: 0.75 }}>
                {axis.toUpperCase()}
                <input
                  // Y in finer steps than the ground plane: X and Z are being
                  // dropped by pointer across a 240-unit map, but Y is only ever
                  // correcting a model's own origin — a nudge, not a move.
                  type="number" step={axis === 'y' ? '0.05' : '1'}
                  value={axis === 'y' ? yValue : (pos[axis] ?? '')}
                  placeholder="—"
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!Number.isFinite(v)) return;
                    // Seed the other axes too, or typing one on a never-placed
                    // venue would save it at 0 on the others.
                    if (axis === 'y') { setY(v); return; }
                    setDraft(room, { x: pos.x ?? 0, z: pos.z ?? 0, [axis]: v });
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2, padding: '4px 5px', borderRadius: 6,
                    background: 'rgba(255,178,94,0.1)', color: '#ffd27d', border: '1px solid rgba(255,178,94,0.35)',
                    font: '11px system-ui', fontVariantNumeric: 'tabular-nums' }}
                />
              </label>
            ))}
          </div>
          {/* HEIGHT IS A NUDGE, SO IT GETS BUTTONS.
              Auto-grounding drops a model's LOWEST point to zero, which is right
              only when that point is the floor — a foundation slab, a kerb or a
              stray vertex under the building puts the visible floor above the
              street instead. No measurement can tell which, so this is the one
              axis that stays manual. */}
          <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }}>
            {[-0.5, -0.1, +0.1, +0.5].map((d) => (
              <button key={d} style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 11 }}
                onClick={() => setY((yValue) + d)}>
                {d > 0 ? `+${d}` : d}
              </button>
            ))}
            <button style={{ ...btn, width: 46, padding: '4px 0', fontSize: 11 }}
              title="Back to auto-grounded"
              onClick={() => setY(0)}>
              0
            </button>
          </div>
        </div>
      )}

      {/* FACING. A virtual room can be authored pointing anywhere, because you
          arrive looking at whatever its spawn points at. A SITED one has to agree
          with the street it stands on, and nothing in the geometry says which
          wall is the front — so this is the control that decides where the
          shopfront and its windows end up. The stub line on the ghost shows the
          direction; 90° steps first because streets are square, fine nudges for
          the ones that are not. */}
      {room && (
        <div style={{ marginBottom: 9 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, opacity: 0.7, marginBottom: 3 }}>
            <span>Facing</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{deg}°</span>
          </div>
          <input
            type="range" min="0" max="359" step="1" value={deg}
            onChange={(e) => setDraft(room, { rotY: (parseFloat(e.target.value) * Math.PI) / 180 })}
            style={{ width: '100%', accentColor: '#ffb25e' }}
          />
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
              onClick={() => setDraft(room, { rotY: turn(rotY, -90) })}>⟲ 90°</button>
            <button style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
              onClick={() => setDraft(room, { rotY: turn(rotY, 90) })}>90° ⟳</button>
            <button style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
              onClick={() => setDraft(room, { rotY: turn(rotY, -5) })}>⟲ 5°</button>
            <button style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
              onClick={() => setDraft(room, { rotY: turn(rotY, 5) })}>5° ⟳</button>
          </div>
        </div>
      )}

      {/* SCALE. Live: the ghost resizes as you drag, and if you happen to be
          standing in the room it resizes around you too — which is the only
          honest way to judge a size, since "does it fit the block" and "does it
          feel right from inside" are different questions with one answer.
          Saved on the row's `size` column, so it travels with the position. */}
      {room && (
        <div style={{ marginBottom: 9 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, opacity: 0.7, marginBottom: 3 }}>
            <span>Scale</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>×{scale.toFixed(2)}</span>
          </div>
          <input
            type="range" min="0.05" max="2" step="0.01" value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            style={{ width: '100%', accentColor: '#ffb25e' }}
          />
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            {[0.25, 0.5, 1].map((v) => (
              <button key={v} style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
                onClick={() => setScale(v)}>×{v}</button>
            ))}
            <button style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
              onClick={() => setScale(Math.round(scaleValue * 100) / 100 - 0.01)}>−</button>
            <button style={{ ...btn, flex: 1, padding: '4px 0', fontSize: 10.5 }}
              onClick={() => setScale(Math.round(scaleValue * 100) / 100 + 0.01)}>+</button>
          </div>
          {fp && (
            <div style={{ fontSize: 10.5, opacity: 0.8, marginTop: 5, fontVariantNumeric: 'tabular-nums' }}>
              {(fp[0] * scale).toFixed(1)} × {(fp[2] * scale).toFixed(1)} on the ground
              {' · '}
              {/* Ceiling against a 1.6u eye is the number that decides whether a
                  room feels like a room or like a cathedral you were shrunk into. */}
              <span style={{ color: fp[1] * scale > 12 ? '#ff9d7d' : fp[1] * scale < 2.4 ? '#ff9d7d' : '#9dff7d' }}>
                ceiling {(fp[1] * scale).toFixed(1)}u
              </span>
              <span style={{ opacity: 0.6 }}> vs 1.6u eye</span>
            </div>
          )}
        </div>
      )}

      {/* THE VERDICT. The whole reason the brush exists is to answer this before
          the click rather than after the reload. */}
      <div style={{
        fontSize: 11, lineHeight: 1.5, padding: '8px 9px', borderRadius: 8, marginBottom: 9,
        background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,178,94,0.22)',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {!room && <span style={{ opacity: 0.6 }}>No brush. Pick a venue above.</span>}
        {room && !hover && <span style={{ opacity: 0.6 }}>Move the pointer over the map…</span>}
        {room && hover && (
          <>
            <div>at <b>{hover.x}, {hover.z}</b></div>
            <div style={{ opacity: 0.8, wordBreak: 'break-all' }}>
              over {hover.mesh ? <b>{hover.mesh}</b> : <span style={{ opacity: 0.6 }}>open ground</span>}
              {hover.meshSize ? ` · ${hover.meshSize[0].toFixed(0)}×${hover.meshSize[2].toFixed(0)}` : ''}
            </div>
            {fp && (
              <div style={{ marginTop: 3, color: over == null ? '#ffe9c4' : (over > 1.05 ? '#ff9d7d' : '#9dff7d') }}>
                shop {fp[0].toFixed(0)}×{fp[2].toFixed(0)}
                {over == null ? '' : over > 1.05 ? ` · ${over.toFixed(1)}× OVER` : ' · fits'}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 5 }}>
        <button disabled={busy || !pending.length}
          style={{ ...btn, flex: 1, background: pending.length ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: pending.length ? '#1c1206' : '#ffd27d' }}
          onClick={save}>{busy ? '…' : `Save ${pending.length || ''}`}</button>
        <button style={{ ...btn, width: 66 }} onClick={reset} disabled={busy || !room}>reset</button>
      </div>
      {msg && <div style={{ fontSize: 10.5, marginTop: 8, color: /saved|reset/.test(msg) ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}

      <div style={{ opacity: 0.55, fontSize: 10.5, marginTop: 9, lineHeight: 1.4 }}>
        Click the map to drop the outline; drag still pans. A venue must be
        visited once before it can be measured. Saved anchors beat both
        rooms.js <code>anchorMesh</code> and the sampler.
      </div>
    </div>
  );
}
